import { AlertCircle, ArrowUpDown, Plus, WalletCards } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { OrganizationVerificationBanner } from "@/modules/verification/components/OrganizationVerificationBanner";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { ActivateWalletModal } from "../components/ActivateWalletModal";
import { ActiveWalletCard } from "../components/ActiveWalletCard";
import { OrgWalletsReader } from "../components/OrgWalletsReader";
import { WalletOrderEditor } from "../components/WalletOrderEditor";
import { WalletsHeader } from "../components/WalletsHeader";
import { WalletsStatsStrip } from "../components/WalletsStatsStrip";
import { isCryptoCurrency } from "../constants/walletMethods.constants";
import { isActivatableWalletCurrency } from "../constants/wallets.constants";
import { useRuntimeWalletActivity } from "../hooks/useRuntimeWalletActivity";
import { useRuntimeWallets } from "../hooks/useRuntimeWallets";
import { useWalletArrangement } from "../hooks/useWalletArrangement";

export const WalletsPage = () => {
  const { t } = useTranslations("wallets");
  const { t: tGlobal } = useTranslations("global");
  const { user, isInternalUser, can } = useAuth();

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_billeteras") },
      { label: tGlobal("sidebar_menu_wallets_home"), to: "/app/wallets" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  const {
    wallets: allWallets,
    isLoading,
    hasAccess,
    error,
    refresh,
    refreshOne: refreshOneWallet,
    applyOrder,
    dropWallet,
  } = useRuntimeWallets();
  const { saveOrder, isSavingOrder } = useWalletArrangement();
  const [isOrdering, setIsOrdering] = useState(false);
  const canArrange = can("payments:write");
  /*
   * PROVISIONAL — see `ACTIVATABLE_WALLET_CURRENCIES`. The filter sits HERE and not in `useRuntimeWallets`
   * because that hook also feeds the payout-batch setup and detail pages, which must keep seeing every
   * wallet the organization holds. The gate is a decision of this view, so the hook stays truthful.
   *
   * Consequence to know before reading a bug report: an organization whose ONLY wallet is in a withheld
   * currency now falls into the "activate your first wallet" empty state, and its balance is off screen
   * while the money is still in the ledger. That is the accepted trade-off of hiding without exception; the
   * statement route (`/app/wallets/:id/movements`) still resolves if the id is known.
   */
  const wallets = useMemo(
    () => allWallets.filter((wallet) => isActivatableWalletCurrency(wallet.currency)),
    [allWallets],
  );
  const {
    activityByWallet,
    isLoadingActivity,
    refreshOne: refreshOneActivity,
  } = useRuntimeWalletActivity(wallets);
  const [isActivateOpen, setIsActivateOpen] = useState(false);

  const stats = useMemo(() => {
    const fiatWallets = wallets.filter((w) => !isCryptoCurrency(w.currency));
    const activeCountries = new Set(fiatWallets.map((w) => w.currency)).size;
    const cryptoNetworks = wallets.filter((w) => isCryptoCurrency(w.currency)).length;
    return { activeCountries, cryptoNetworks };
  }, [wallets]);

  // From the UNFILTERED list: this is what stops the modal offering a currency the organization already
  // holds, and a wallet hidden from the grid is still activated.
  const activatedCurrencies = useMemo(() => allWallets.map((w) => w.currency), [allWallets]);

  // One card's refresh reads only that wallet and its own recent legs — balance and movements together,
  // since a balance that moved without its movement showing is exactly what makes people press it twice.
  // It used to call the list `refresh()`, which re-read every wallet and flipped the page-level loading
  // state, blanking the whole grid to update one card.
  const refreshWallet = useCallback(
    async (walletId: string) => {
      await Promise.all([refreshOneWallet(walletId), refreshOneActivity(walletId)]);
    },
    [refreshOneWallet, refreshOneActivity],
  );

  const handleSaveOrder = useCallback(
    async (walletIds: string[]) => {
      try {
        await saveOrder(walletIds);
        applyOrder(walletIds);
        colocaToast.success(t("order_saved"));
        setIsOrdering(false);
      } catch (err: unknown) {
        colocaToast.error(HttpApiService.getErrorMessage(err) || t("order_save_error"));
      }
    },
    [saveOrder, applyOrder, t],
  );

  // Access is decided by the backend RBAC (`payments:read`): a 401/403 latches and renders NoAccessView.
  if (!hasAccess) {
    return <NoAccessView />;
  }

  // INTERNAL are not scoped to their own org — they read a client org's wallets via the org-selector reader.
  if (isInternalUser) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
        <OrgWalletsReader />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <span className="text-sm text-muted-foreground">{t("loading")}</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-4 p-2">
        <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => void refresh()}
            className="rounded-md border border-border bg-background px-4 py-2 text-sm text-foreground transition-colors hover:bg-muted"
          >
            {t("retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <OrganizationVerificationBanner />

      <WalletsHeader userName={user?.full_name ?? ""} activeCount={wallets.length} />

      {wallets.length > 0 && (
        <WalletsStatsStrip activeCountries={stats.activeCountries} cryptoNetworks={stats.cryptoNetworks} />
      )}

      {wallets.length === 0 ? (
        <ColocaEmpty
          icon={WalletCards}
          title={t("empty_activate_title")}
          description={t("empty_activate_desc")}
        >
          <ColocaButton variant="primary" icon={Plus} onClick={() => setIsActivateOpen(true)}>
            {t("empty_activate_cta")}
          </ColocaButton>
        </ColocaEmpty>
      ) : isOrdering ? (
        <WalletOrderEditor
          wallets={wallets}
          isSaving={isSavingOrder}
          onSave={(walletIds) => void handleSaveOrder(walletIds)}
          onCancel={() => setIsOrdering(false)}
        />
      ) : (
        <section className="space-y-3">
          {/* Wraps: with "Ordenar" beside "Activar wallet" a phone left the heading under 100px. */}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-0 flex-1 basis-48">
              <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
                {t("section_active")}
                <span className="rounded border border-primary/20 bg-primary/10 px-1.5 py-0.5 font-mono text-[11px] text-primary">
                  {wallets.length}
                </span>
              </h2>
              <p className="mt-0.5 text-xs text-muted-foreground">{t("section_active_sub")}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {canArrange && wallets.length > 1 && (
                <ColocaButton
                  size="sm"
                  variant="ghost"
                  icon={ArrowUpDown}
                  onClick={() => setIsOrdering(true)}
                >
                  {t("order_cta")}
                </ColocaButton>
              )}
              <ColocaButton variant="primary" size="sm" icon={Plus} onClick={() => setIsActivateOpen(true)}>
                {t("activate_cta")}
              </ColocaButton>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3">
            {wallets.map((wallet) => (
              <ActiveWalletCard
                key={wallet.id}
                wallet={wallet}
                recent={activityByWallet[wallet.id] ?? []}
                isLoadingRecent={isLoadingActivity}
                onRefresh={refreshWallet}
                onDeleted={dropWallet}
                // Every wallet, the withheld currencies included: a USD balance can still fund a send.
                sendOrigins={allWallets}
              />
            ))}
          </div>
        </section>
      )}

      <ActivateWalletModal
        open={isActivateOpen}
        onClose={() => setIsActivateOpen(false)}
        activatedCurrencies={activatedCurrencies}
        onActivated={refresh}
      />
    </div>
  );
};
